import {
  TruckStopGetCompanyResponse,
  TruckStopService,
} from '@/@types/generated'
import Loading from '@/components/Atoms/Loading'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

interface TruckStopCompanyContextProps {
  company: TruckStopGetCompanyResponse
  reload: (background?: boolean) => Promise<void>
}

const TruckStopCompanyContext = createContext<TruckStopCompanyContextProps>(
  undefined!
)

export function useTruckStopCompany() {
  const context = useContext(TruckStopCompanyContext)
  if (context === undefined) {
    throw new Error(
      'useTruckStopCompany must be used within a TruckStopCompanyProvider'
    )
  }

  return context
}

export function TruckStopCompanyProvider({
  id,
  children,
  onError,
}: React.PropsWithChildren<{
  id: string
  onError?: (error: unknown) => void
}>) {
  // State
  const [loading, setLoading] = useState(true)
  const [company, setCompany] = useState<TruckStopGetCompanyResponse | null>(
    null
  )

  // Load the company details
  const reload = useCallback(
    async (background?: boolean) => {
      if (!id) return
      if (!background) setLoading(true)

      try {
        const company = await TruckStopService.getCompany(id)
        setCompany(company)
      } catch (error) {
        onError?.(error)
      } finally {
        setLoading(false)
      }
    },
    [id, onError]
  )

  // Load company details when ID changes
  useEffect(() => {
    reload()
  }, [reload])

  // No company yet, show loading
  if (loading)
    return (
      <Loading variant="fullscreen" message="Retrieving company details..." />
    )
  if (!company) return <div>Company not found</div>

  return (
    <TruckStopCompanyContext.Provider
      value={{
        company,
        reload,
      }}
    >
      {children}
    </TruckStopCompanyContext.Provider>
  )
}
